import { useState } from 'react'
import type { JSX } from 'react'
import { useTranslation } from 'react-i18next'
import { LanguagePicker } from '@rs/ui'

/**
 * ══ `/ncc/doi-mat-khau` — MÀN ĐỔI MẬT KHẨU CỦA THẦU PHỤ ═════════════════════════════════════
 *
 * ⚠ Chỉ cho tài khoản THẦU PHỤ (`vendor_user`). Bảy vai nội bộ (từ E13 sửa 19/10/2026 cũng có mật khẩu `app_user`, migration
 *   `0177`) đổi ở Cài đặt ▸ 🔒 Mật khẩu / nút 🔒 đầu trang (`features/password/PasswordCard.tsx`) — cửa khác, Cedar khác.
 *
 * ⚠ Màn này KHÔNG kiểm luật mật khẩu. `POST /api/v1/ncc/auth/doi-mat-khau` (đã có từ đợt 0) gọi
 *   `passwordRuleError` của `@rs/identity` và trả về câu chữ đầy đủ — kiểm lại ở đây là dựng bản
 *   sao thứ hai của luật rồi hai bản lệch nhau.
 *
 * ⚠ Đổi xong thì MỌI PHIÊN bị huỷ (kể cả phiên đang gọi) — máy chủ xoá cookie. Màn đưa người dùng
 *   về `/ncc/login` để đăng nhập lại, đúng hành vi đó chứ không giả vờ còn đăng nhập.
 */
export const VendorChangePasswordPage = (): JSX.Element => {
  // Nhóm `login` nằm trong `STARTUP_KEY_GROUPS` ⇒ có sẵn, không cần `loadKeyGroups`. Đây là màn
  // người CHƯA đăng nhập cũng mở được (mật khẩu tạm — E17), nên không được chờ nạp trễ.
  const { t } = useTranslation()
  /**
   * Luồng ÉP đổi (E17) đến từ `/ncc/login` kèm `?bat-buoc=1`. Không suy ra từ `me === null`: sau
   * khi đăng nhập, phiên ĐÃ được cấp (máy chủ đặt cookie trước khi trả cờ `mustChangePass`), nên
   * `me` khác null và cảnh báo sẽ biến mất đúng lúc cần nhất.
   */
  const forced = new URLSearchParams(location.search).has('bat-buoc')
  const [oldPassword, setOld] = useState('')
  const [newPassword, setNew] = useState('')
  const [confirm, setConfirm] = useState('')
  const [err, setErr] = useState<string | null>(null)
  const [done, setDone] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault()
    if (submitting) return
    setErr(null)
    // Phép kiểm DUY NHẤT ở màn: hai ô nhập lại phải khớp. Đây không phải luật mật khẩu — máy chủ
    // không thể kiểm hộ vì nó chỉ nhận MỘT ô.
    if (newPassword !== confirm) {
      setErr(t('login.nccPw.mismatch'))
      return
    }
    setSubmitting(true)
    try {
      const res = await fetch('/api/v1/ncc/auth/doi-mat-khau', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ oldPassword, newPassword }),
      })
      if (!res.ok) {
        const body = await res.json().catch(() => null) as { message?: string } | null
        setErr(body?.message ?? `Máy chủ trả mã ${res.status}.`)
        return
      }
      setDone(true)
    } catch {
      setErr(t('login.ncc.netError'))
    } finally {
      setSubmitting(false)
    }
  }

  const inCls = 'w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100'
  const lblCls = 'mb-1 block text-xs font-semibold text-slate-500'
  const btnCls = 'w-full rounded-lg bg-gradient-to-r from-brand-500 to-brand-600 px-4 py-2.5 text-sm font-bold text-white hover:from-brand-600 hover:to-brand-700 disabled:opacity-40'

  return (
    /* Cùng khung nhận diện với `/ncc/login` (`vendor-login.tsx`) — màn của NGƯỜI NGOÀI công ty, không
       được để trần (ảnh user 25/09/2026: chữ đen trên nền trắng, ô nhập không viền). */
    <main className="mx-auto w-full max-w-[380px] px-4 py-[10vh]">
      <header className="mb-3 flex justify-end">
        <LanguagePicker />
      </header>
      <div className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-xl">
        <div className="bg-gradient-to-r from-brand-500 to-sky-500 px-6 py-7 text-center">
          <img src="/RT-LOGO-header.jpg" alt={t('app.name')}
               className="mx-auto mb-2 h-12 w-auto rounded-lg bg-white object-contain px-2 py-1" />
          <div className="text-sm text-brand-50">{t('app.name')}</div>
        </div>
        <div className="space-y-4 px-6 py-6">
          <h1 className="text-lg font-bold text-slate-800">{t('login.nccPw.title')}</h1>

          {done ? (
            <>
              <p role="status" className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-[13px] text-emerald-700">
                {t('login.nccPw.done')}
              </p>
              <button type="button" className={btnCls} onClick={() => { location.assign('/ncc/login') }}>
                {t('login.nccPw.backToLogin')}
              </button>
            </>
          ) : (
            <>
              {forced && (
                <p role="alert" className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[13px] text-amber-800">
                  {t('login.nccPw.forced')}
                </p>
              )}
              <form onSubmit={(e) => void submit(e)} className="space-y-4">
                <div>
                  <label htmlFor="ncc-old" className={lblCls}>{t('login.nccPw.oldLabel')}</label>
                  <input id="ncc-old" type="password" value={oldPassword} required className={inCls}
                         autoComplete="current-password" onChange={(e) => { setOld(e.target.value) }} />
                </div>
                <div>
                  <label htmlFor="ncc-new" className={lblCls}>{t('login.nccPw.newLabel')}</label>
                  <input id="ncc-new" type="password" value={newPassword} required className={inCls}
                         autoComplete="new-password" onChange={(e) => { setNew(e.target.value) }} />
                </div>
                <div>
                  <label htmlFor="ncc-new2" className={lblCls}>{t('login.nccPw.confirmLabel')}</label>
                  <input id="ncc-new2" type="password" value={confirm} required className={inCls}
                         autoComplete="new-password" onChange={(e) => { setConfirm(e.target.value) }} />
                </div>
                {err !== null && (
                  <p role="alert" className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[13px] text-rose-700">{err}</p>
                )}
                <button type="submit" disabled={submitting} className={btnCls}>
                  {submitting ? t('login.nccPw.submitting') : t('login.nccPw.submit')}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </main>
  )
}
